import { computed, makeObservable, observable } from "mobx"
import { htmlToTextTransform } from "@lib/html"
import value1 from "@assets/media/our-values-1.png"
import value2 from "@assets/media/our-values-2.png"
import value3 from "@assets/media/our-values-3.png"

import { BaseModel } from "./base.model"

import type { Localization } from "@serializers/localization"
import type { StaticImageData } from "next/image"

export interface TeamMember {
  id: string | number
  name: string
  main_photo: string
  captain_photo?: string
  priority: number
  position: string
  is_captain: boolean
  location_id: number | null
}

export interface TeamReview {
  text: string
  image: string
}

export interface TeamLocation {
  id: number
  title: string
  image: string
  members: number[]
}

interface OurValue {
  template: "lips" | "mask" | "whip"
  title: string
  text: string
  image: StaticImageData
  className?: string
}

export class TeamPageModel extends BaseModel {
  // @ts-ignore
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  @observable translations: Localization[] = []
  @observable staff = [] as TeamMember[]
  @observable locations: TeamLocation[] = []
  @observable reviews: TeamReview[] = []

  @computed get captains() {
    return this.staff.filter(
      (member) => member.is_captain && !!member.captain_photo,
    )
  }

  @computed get ourValues(): OurValue[] {
    return [
      {
        template: "lips",
        title: this.getTemplate("values.1.title"),
        text: this.getTemplate("values.1.text"),
        image: value1,
        className: "laptop:mr-12 -rotate-[8deg] origin-bottom-right",
      },
      {
        template: "whip",
        title: this.getTemplate("values.2.title"),
        text: this.getTemplate("values.2.text"),
        image: value2,
        className: "laptop:mr-12",
      },
      {
        template: "mask",
        title: this.getTemplate("values.3.title"),
        text: this.getTemplate("values.3.text"),
        image: value3,
        className: "rotate-[8deg] origin-bottom-left",
      },
    ]
  }

  getMembersByLocationId = (id: number) => {
    return this.staff.filter((member) => member.location_id === id)
  }

  getTeamPage = async () => {
    try {
      const {
        data: { translations },
        staff,
        locations,
        reviews,
      } = await this.Providers.TeamPageProvider.getTeamPage(
        this.Models.GlobalModel.lang,
      )

      this.applyData({
        staff,
        translations,
        locations,
        reviews,
      })
    } catch (e) {}
  }

  getTemplate = (key: string, simpleText = true) => {
    const find = this.translations.find((template) => template.key === key)
    if (find) {
      return simpleText ? htmlToTextTransform(find.template) : find.template
    }
    return ""
  }

  hydrate = (data: TeamPageModel) => {
    this.applyData({
      translations: data.translations,
      staff: data.staff,
      locations: data.locations,
      reviews: data.reviews,
    })
  }

  serialize = () => {
    return {
      translations: this.translations,
      staff: this.staff,
      locations: this.locations,
      reviews: this.reviews,
    }
  }
}
